import { WarningDialog } from '@strata/react-dialog';
import { FormContainer } from '@strata/react-form-container';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { PrescriberDetailsFormContent } from 'pages/npiManagement/PrescriberDetailsFormContent';
import { usePrescriberLoad } from 'pages/npiManagement/usePrescriberLoad';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { get, post, put } from 'services/Http';
import { loadPrescriberValues, packagePrescriber, prescriberSchema } from 'services/NPISupport';

export const PrescriberDetails = () => {
    const { npi } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate('/prescribers');

    const isCreate = npi === CREATE_ROUTE;

    const [showWarning, setShowWarning] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { loadState, prescriber, setPrescriber, isReactivating, handleReactivate } =
        usePrescriberLoad(npi, isCreate);

    const isDeactivated = loadState.kind === 'deactivated';
    const isReadOnly = isDeactivated;
    const isPageLoading = !isCreate && loadState.kind === 'loading';
    const hasPracticeNpi = !isCreate && prescriber?.practice_npi;

    const restoreOrderlyInfo = async (form) => {
        try {
            setIsLoading(true);
            await post(`/reference/prescribers/${form.values.npi}/run_orderly`);
            setPrescriber(await get(`/reference/prescribers/${form.values.npi}`));
            setShowWarning(false);
            await SuccessToast({ message: 'Prescriber successfully restored to default.' });
        } catch (err) {
            handleError('Error restoring prescriber to default.', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Formik
            initialValues={loadPrescriberValues(prescriber)}
            validationSchema={prescriberSchema}
            enableReinitialize
            onSubmit={async (values) => {
                try {
                    const body = packagePrescriber(values);
                    if (isCreate) {
                        await post('/reference/prescribers/', body);
                    } else {
                        await put(`/reference/prescribers/${values.npi}`, body);
                    }
                    goHome();
                } catch (err) {
                    handleError('Error saving prescriber.', err);
                }
            }}
        >
            {(formik) => (
                <>
                    <WarningDialog
                        dialogTitle="Restore to Default"
                        isOpen={showWarning}
                        primaryButtonLabel="Yes, Continue"
                        secondaryButtonLabel="No, Cancel"
                        onSecondaryButtonClick={() => setShowWarning(false)}
                        onPrimaryButtonClick={() => restoreOrderlyInfo(formik)}
                        primaryButtonDisabled={isLoading}
                        dialogContent={
                            <>
                                <p>
                                    This will revert any overwritten data back to what is received
                                    from Orderly.
                                </p>
                                <p>Are you sure you want to continue?</p>
                            </>
                        }
                    />
                    <FormContainer
                        formTitle={`${isCreate ? 'Create' : 'Edit'} Prescriber`}
                        isLoading={isPageLoading}
                        onFormSubmit={formik.handleSubmit}
                        isSubmitting={
                            isReadOnly ||
                            loadState.kind === 'not_found' ||
                            loadState.kind === 'error' ||
                            formik.isSubmitting ||
                            !(formik.dirty && formik.isValid)
                        }
                        onSecondaryButtonClick={goHome}
                        formLeftContent={
                            <PrescriberDetailsFormContent
                                npi={npi}
                                loadState={loadState}
                                prescriber={prescriber}
                                formik={formik}
                                isCreate={isCreate}
                                isReadOnly={isReadOnly}
                                isDeactivated={isDeactivated}
                                hasPracticeNpi={hasPracticeNpi}
                                isReactivating={isReactivating}
                                onReactivate={handleReactivate}
                                onRestoreOrderly={() => setShowWarning(true)}
                            />
                        }
                    />
                </>
            )}
        </Formik>
    );
};
